import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { LineReveal } from "./motion/Reveal";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const BrowserFrame = ({ className, children, delay = 0, style }) => (
  <motion.div
    initial={{ opacity: 0, y: 60, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 1.4, delay, ease: EASE }}
    style={style}
    className={`absolute bg-white border border-taupe-line shadow-[0_40px_80px_-40px_rgba(28,27,26,.35)] overflow-hidden ${className}`}
  >
    <div className="flex items-center gap-1.5 px-3 h-7 border-b border-taupe-line bg-ivory">
      <span className="h-1.5 w-1.5 rounded-full bg-taupe-line" /><span className="h-1.5 w-1.5 rounded-full bg-taupe-line" /><span className="h-1.5 w-1.5 rounded-full bg-taupe-line" />
      <span className="ml-3 h-2 w-24 rounded-full bg-sand" />
    </div>
    {children}
  </motion.div>
);

const Line = ({ w, className = "" }) => <span className={`block h-[3px] rounded-full bg-sand ${className}`} style={{ width: w }} />;

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 20 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="home" data-testid="hero-section" ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative min-h-[100svh] flex flex-col justify-end lg:justify-center overflow-hidden pt-28 pb-14 lg:pt-24 lg:pb-24">
      <div className="absolute inset-0 -z-10">
        <div className="absolute right-[-10%] top-[10%] h-[60vh] w-[60vh] rounded-full bg-blush/70 blur-[120px]" />
        <div className="absolute left-[-15%] bottom-[-10%] h-[50vh] w-[50vh] rounded-full bg-sand blur-[120px]" />
      </div>

      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="eyebrow mb-8 flex items-center gap-4">
            <span className="h-px w-10 bg-sage/60" /> Web Design Studio · Bangalore
          </motion.p>
          <LineReveal
            data-testid="hero-title"
            as="h1"
            delay={0.45}
            lines={["Your Business", "Deserves a Website", <><span className="italic font-normal text-sage">Worth</span> Exploring.</>]}
            className="display text-[13vw] sm:text-[9.5vw] lg:text-[5.6vw] xl:text-[5.2vw] text-charcoal"
          />
          <motion.p data-testid="hero-subtitle" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease: EASE }} className="body-lg mt-8 max-w-md">
            Thoughtfully designed digital experiences that turn attention into connection.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta-start" onClick={() => scrollToId("contact")} className="btn btn-dark"><span>Start Your Project</span></button>
            <button data-testid="hero-cta-explore" onClick={() => scrollToId("work")} className="btn btn-outline group"><span className="flex items-center gap-2">Explore Our Work <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span></button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yArt, perspective: 1200 }} className="lg:col-span-5 relative h-[380px] sm:h-[460px] lg:h-[560px]" data-testid="hero-browser-preview" aria-hidden>
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="absolute inset-0">
            <BrowserFrame delay={0.9} className="left-0 right-4 lg:right-0 top-4 bottom-16 lg:bottom-10" style={{ transform: "translateZ(0px)" }}>
              <div className="p-5 sm:p-7 h-full flex flex-col">
                <div className="flex justify-between items-center">
                  <span className="font-serif tracking-[0.3em] text-xs text-charcoal">MAISON</span>
                  <div className="flex gap-2"><Line w={24} /><Line w={24} /><Line w={24} /></div>
                </div>
                <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.7, duration: 1 }} className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1] mt-8 text-charcoal">
                  A sanctuary<br /><span className="italic text-taupe">of quiet</span><br />elegance.
                </motion.p>
                <div className="mt-6 space-y-2"><Line w="70%" /><Line w="52%" /></div>
                <div className="mt-auto grid grid-cols-3 gap-3">
                  <div className="aspect-[4/5] bg-blush" /><div className="aspect-[4/5] bg-sand" /><div className="aspect-[4/5] bg-cream" />
                </div>
              </div>
            </BrowserFrame>
            <BrowserFrame delay={1.15} className="right-0 lg:-right-6 bottom-0 w-[58%] sm:w-[52%] h-[45%] animate-float" style={{ transform: "translateZ(60px)" }}>
              <div className="p-4 h-full flex flex-col justify-between bg-charcoal">
                <span className="font-mono text-[9px] tracking-[0.3em] text-ivory/60">ENQUIRE</span>
                <div className="space-y-2"><span className="block h-px w-full bg-ivory/20" /><span className="block h-px w-3/4 bg-ivory/20" /><span className="block h-px w-full bg-ivory/20" /></div>
                <span className="self-start rounded-full bg-ivory text-charcoal font-sans text-[9px] px-3 py-1.5">Send Enquiry</span>
              </div>
            </BrowserFrame>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.5, duration: 1, ease: EASE }} style={{ transform: "translateZ(90px)" }} className="absolute -left-2 sm:-left-6 top-1/2 glass border border-taupe-line px-4 py-3 shadow-lg">
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-sage">Responsive</p>
              <p className="font-serif text-lg text-charcoal mt-0.5">Desktop · Tablet · Mobile</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div style={{ opacity: fade }} className="container-x mt-14 lg:mt-0 lg:absolute lg:bottom-10 lg:left-0 lg:right-0 flex items-center justify-between">
        <motion.button onClick={() => scrollToId("about")} data-testid="hero-scroll-indicator" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-taupe hover:text-charcoal transition-colors">
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}><ArrowDown size={12} /></motion.span> Scroll
        </motion.button>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.28em] text-taupe">Design · Development · Experience</motion.p>
      </motion.div>
    </section>
  );
};
